import React from 'react';
import { X, Calendar, Clock, DollarSign, FileText, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import { ProcessingJob } from '../types';
import { theme } from '../styles/theme';

interface JobDetailsModalProps {
  job: ProcessingJob | null;
  open: boolean;
  onClose: () => void;
}

export const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ job, open, onClose }) => {
  if (!open || !job) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary bg-opacity-50" onClick={onClose}>
      <div 
        className={`${theme.card.background} rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className={`text-2xl font-bold ${theme.text.primary} flex items-center`}>
            <FileText className="w-6 h-6 mr-3" />
            Job Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-subtle rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-88px)]">
          {/* Job Info */}
          <div className="mb-6">
            <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3`}>Job Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Job Name</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.job_name || 'Untitled'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">File Name</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.filename}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Organization</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.organization_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Project Name</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.project_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Status</p>
                <p className={`font-medium ${theme.text.primary} capitalize`}>{job.status}</p>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="mb-6">
            <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3 flex items-center`}>
              <Calendar className="w-5 h-5 mr-2" />
              Timeline
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Created</p>
                <p className={`text-sm ${theme.text.primary}`}>
                  {new Date(job.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Started</p>
                <p className={`text-sm ${theme.text.primary}`}>
                  {job.started_at ? new Date(job.started_at).toLocaleString() : 'Not started'}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Completed</p>
                <p className={`text-sm ${theme.text.primary}`}>
                  {job.completed_at ? new Date(job.completed_at).toLocaleString() : 'Not completed'}
                </p>
              </div>
              {job.started_at && job.completed_at && (
                <div>
                  <p className="text-sm text-text-tertiary mb-1">Duration</p>
                  <p className={`text-sm ${theme.text.primary}`}>
                    {Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000 / 60)} minutes
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Model Configuration */}
          <div className="mb-6">
            <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3 flex items-center`}>
              <Sparkles className="w-5 h-5 mr-2" />
              Model Configuration
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Model</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.model_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Temperature</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.temperature !== undefined ? job.temperature : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Chunk Size</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.chunk_size || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Max Concurrent</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.max_concurrent || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Processing Stats */}
          <div className="mb-6">
            <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3 flex items-center`}>
              <CheckCircle className="w-5 h-5 mr-2" />
              Processing Statistics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Total Rows</p>
                <p className={`font-medium ${theme.text.primary}`}>{(job.total_rows || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Processed Rows</p>
                <p className={`font-medium ${theme.text.primary}`}>{(job.processed_rows || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Progress</p>
                <p className={`font-medium ${theme.text.primary}`}>{job.progress.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Total Tokens</p>
                <p className={`font-medium ${theme.text.primary}`}>{(job.total_tokens || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="mb-6">
            <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3 flex items-center`}>
              <DollarSign className="w-5 h-5 mr-2" />
              Cost Breakdown
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-text-tertiary mb-1">Cost (USD)</p>
                <p className={`text-lg font-bold ${theme.text.primary}`}>
                  ${(parseFloat(job.cost_usd as any) || 0).toFixed(4)}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary mb-1">Cost (SGD)</p>
                <p className={`text-lg font-bold ${theme.text.primary}`}>
                  ${(parseFloat(job.cost_sgd as any) || 0).toFixed(4)}
                </p>
              </div>
            </div>
          </div>

          {/* Prompt Text */}
          {job.prompt_text && (
            <div className="mb-6">
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3`}>Prompt</h3>
              <div className={`${theme.background.tertiary} p-4 rounded-lg border border-border`}>
                <p className={`text-sm ${theme.text.primary} whitespace-pre-wrap`}>{job.prompt_text}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {job.error_message && (
            <div className="mb-6">
              <h3 className={`text-lg font-semibold text-qc-error mb-3 flex items-center`}>
                <AlertCircle className="w-5 h-5 mr-2" />
                Error Details
              </h3>
              <div className={`${theme.status.error.background} p-4 rounded-lg border border-qc-error/30`}>
                <p className="text-sm text-qc-error whitespace-pre-wrap">{job.error_message}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
